/**
 * Feature 252 — several identical portions drawn as one line.
 *
 * A group of one renders exactly as a plain row, with no control and no count,
 * so a board with the switch on is unchanged for dishes ordered once.
 *
 * A group of several renders one collapsed row carrying the count, the least
 * advanced status of its portions, and a control that opens it. Opened, the
 * portions are listed underneath and each is an ordinary row again — which is
 * how cancelling, comping, moving or settling ONE of them keeps working exactly
 * as it does today.
 */
import { useState } from 'react';
import type { DelayTier } from '@shared/kds/table-board';
import { groupedLineStatus, type GroupedLine } from '@shared/kds/grouping';
import TableDishRow from './TableDishRow';
import { useTranslation } from '../i18n';
import type { KdsT } from '../lib/i18n/settings';

interface GroupedDishRowProps {
  line: GroupedLine<any>;
  delayTier?: DelayTier;
  isOldest?: boolean;
  served?: boolean;
  now?: number;
  bar?: boolean;
  onOpenDish?: (item: any) => void;
  /** Feature 268 — the venue shows who each plate is for. */
  showDinerTag?: boolean;
}

/**
 * "2 di 3 pronti" when the portions disagree; nothing when they all agree,
 * because a line whose portions are all at one status has nothing to explain.
 */
export function statusHint(
  counts: Record<string, number>,
  total: number,
  t: KdsT,
): string | null {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length <= 1) return null;
  const ahead = entries
    .filter(([status]) => status === 'READY' || status === 'SERVED')
    .reduce((sum, [, n]) => sum + n, 0);
  if (ahead === 0 || ahead >= total) return null;
  return counts.SERVED
    ? t('kds.group.servedOfTotal', '{{done}} di {{total}} serviti', { done: ahead, total })
    : t('kds.group.readyOfTotal', '{{done}} di {{total}} pronti', { done: ahead, total });
}

export default function GroupedDishRow({
  line,
  delayTier,
  isOldest = false,
  served = false,
  now = Date.now(),
  bar = false,
  onOpenDish,
  showDinerTag = false,
}: GroupedDishRowProps) {
  // Per device and per visit. A cook opening a line to check something must not
  // change what every other screen in the kitchen shows.
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const [first] = line.portions;
  if (!first) return null;

  if (line.count === 1) {
    return (
      <TableDishRow
        showDinerTag={showDinerTag}
        item={first}
        delayTier={delayTier}
        isOldest={isOldest}
        served={served}
        now={now}
        bar={bar}
        onOpenDish={onOpenDish}
      />
    );
  }

  const { status, counts } = groupedLineStatus(
    line.portions.map((p: any) => p?.status ?? 'PENDING'),
  );

  // The collapsed row stands for the line, so it must not claim its own
  // portion's progress: it borrows the least advanced status of the group.
  const representative = { ...first, status };

  return (
    <div className="space-y-2">
      <TableDishRow
        showDinerTag={showDinerTag}
        item={representative}
        delayTier={delayTier}
        isOldest={isOldest}
        served={served}
        now={now}
        bar={bar}
        onOpenDish={onOpenDish}
        groupCount={line.count}
        groupPortions={line.portions}
        groupStatusHint={statusHint(counts, line.count, t)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((open) => !open)}
      />
      {expanded && (
        <div className="pl-4 border-l-2 border-kds-muted/30 space-y-2">
          {line.portions.map((portion: any) => (
            <TableDishRow
              showDinerTag={showDinerTag}
              key={portion.id}
              item={portion}
              delayTier={delayTier}
              served={served}
              now={now}
              bar={bar}
              onOpenDish={onOpenDish}
            />
          ))}
        </div>
      )}
    </div>
  );
}
